import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Save, Trash2, ArrowUp, ArrowDown, Loader2, Eye, EyeOff, Tags } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/api';

const slugify = (s) =>
  (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const CategoriesAdmin = () => {
  const { toast } = useToast();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.categories.list();
      setCats(data || []);
    } catch (e) {
      toast({ title: 'Failed to load categories', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const patchLocal = (id, patch) =>
    setCats((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const addCategory = async () => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await adminApi.categories.create({ name, slug: slugify(name), active: true });
      setNewName('');
      toast({ title: 'Category added' });
      await load();
    } catch (e) {
      toast({ title: 'Could not add category', description: e.message, variant: 'destructive' });
    } finally {
      setAdding(false);
    }
  };

  const saveRow = async (cat) => {
    if (!cat.name.trim()) {
      toast({ title: 'Name cannot be empty', variant: 'destructive' });
      return;
    }
    setSavingId(cat.id);
    try {
      await adminApi.categories.update(cat.id, {
        name: cat.name.trim(),
        slug: slugify(cat.slug || cat.name),
        active: cat.active,
        order: cat.order,
      });
      toast({ title: 'Saved' });
      await load();
    } catch (e) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' });
    } finally {
      setSavingId('');
    }
  };

  const toggleActive = async (cat, value) => {
    patchLocal(cat.id, { active: value });
    try {
      await adminApi.categories.update(cat.id, {
        name: cat.name, slug: cat.slug, active: value, order: cat.order,
      });
      toast({ title: value ? 'Category is now visible' : 'Category hidden from the site' });
    } catch (e) {
      patchLocal(cat.id, { active: !value });
      toast({ title: 'Update failed', description: e.message, variant: 'destructive' });
    }
  };

  const move = async (index, dir) => {
    const next = [...cats];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setCats(next);
    setReordering(true);
    try {
      await adminApi.categories.reorder(next.map((c) => c.id));
    } catch (e) {
      toast({ title: 'Reorder failed', description: e.message, variant: 'destructive' });
      await load();
    } finally {
      setReordering(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await adminApi.categories.remove(toDelete.id);
      toast({ title: 'Category deleted' });
      setToDelete(null);
      await load();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.message, variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#EADBC8] mb-2 flex items-center gap-2">
          <Tags size={26} className="text-[#C5A880]" /> Portfolio Categories
        </h1>
        <p className="text-[#EADBC8]/60">
          Create, rename, reorder, hide and delete the categories used to tag events and filter the public portfolio.
          Hidden categories and categories with no published events never appear publicly.
        </p>
      </div>

      {/* Add new */}
      <Card className="bg-[#2A2420] border-[#C5A880]/20 mb-6">
        <CardContent className="p-5">
          <label className="block text-[#EADBC8] text-sm font-medium mb-2">Add a category</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCategory()}
              placeholder="e.g. Pets & Animals"
              className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8] placeholder:text-[#EADBC8]/30"
              data-testid="new-category-input"
            />
            <Button
              onClick={addCategory}
              disabled={adding || !newName.trim()}
              className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold whitespace-nowrap"
              data-testid="add-category-btn"
            >
              {adding ? <Loader2 size={18} className="mr-2 animate-spin" /> : <Plus size={18} className="mr-2" />}
              Add Category
            </Button>
          </div>
          {newName.trim() && (
            <p className="text-[#EADBC8]/40 text-xs mt-2">URL slug: <span className="text-[#C5A880]">{slugify(newName)}</span></p>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="text-[#C5A880] animate-spin" size={32} /></div>
      ) : (
        <div className="space-y-3" data-testid="categories-list">
          {cats.map((cat, idx) => (
            <Card key={cat.id} className={`bg-[#2A2420] border ${cat.active ? 'border-[#C5A880]/20' : 'border-[#C5A880]/10 opacity-70'}`} data-testid={`category-row-${cat.slug}`}>
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                  {/* Reorder */}
                  <div className="flex lg:flex-col gap-1">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0 || reordering}
                      className="p-1.5 rounded bg-[#1A1612] text-[#EADBC8]/70 hover:text-[#C5A880] disabled:opacity-30"
                      data-testid={`move-up-${cat.slug}`}><ArrowUp size={16} /></button>
                    <button onClick={() => move(idx, 1)} disabled={idx === cats.length - 1 || reordering}
                      className="p-1.5 rounded bg-[#1A1612] text-[#EADBC8]/70 hover:text-[#C5A880] disabled:opacity-30"
                      data-testid={`move-down-${cat.slug}`}><ArrowDown size={16} /></button>
                  </div>

                  {/* Name + slug */}
                  <div className="flex-1 grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[#EADBC8]/50 text-xs mb-1">Name</label>
                      <Input value={cat.name} onChange={(e) => patchLocal(cat.id, { name: e.target.value })}
                        className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8]" data-testid={`category-name-${cat.slug}`} />
                    </div>
                    <div>
                      <label className="block text-[#EADBC8]/50 text-xs mb-1">URL slug</label>
                      <Input value={cat.slug} onChange={(e) => patchLocal(cat.id, { slug: e.target.value })}
                        className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8]" data-testid={`category-slug-${cat.slug}`} />
                    </div>
                  </div>

                  {/* Meta + actions */}
                  <div className="flex items-center justify-between lg:justify-end gap-4">
                    <span className="text-[#EADBC8]/50 text-xs whitespace-nowrap">{cat.event_count} event{cat.event_count === 1 ? '' : 's'}</span>
                    <div className="flex items-center gap-2" title={cat.active ? 'Visible' : 'Hidden'}>
                      {cat.active ? <Eye size={16} className="text-[#C5A880]" /> : <EyeOff size={16} className="text-[#EADBC8]/40" />}
                      <Switch checked={cat.active} onCheckedChange={(v) => toggleActive(cat, v)} data-testid={`category-active-${cat.slug}`} />
                    </div>
                    <Button size="sm" onClick={() => saveRow(cat)} disabled={savingId === cat.id}
                      className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold" data-testid={`save-category-${cat.slug}`}>
                      {savingId === cat.id ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setToDelete(cat)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`delete-category-${cat.slug}`}>
                      <Trash2 size={15} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {cats.length === 0 && (
            <p className="text-[#EADBC8]/50 text-center py-8">No categories yet — add your first one above.</p>
          )}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#2A2420] border-[#C5A880]/30 text-[#EADBC8]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#EADBC8]">Delete "{toDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#EADBC8]/70">
              {toDelete?.event_count > 0 ? (
                <>
                  <span className="text-red-400 font-medium">{toDelete.event_count} portfolio event{toDelete.event_count === 1 ? '' : 's'}</span>{' '}
                  currently use this category. Deleting it will remove it from the portfolio filters — those events and their photos are kept, but they will no longer be tagged with this category.
                </>
              ) : (
                'No events use this category. It will be permanently removed.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#C5A880]/30 text-[#EADBC8] hover:bg-[#1A1612]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-category">
              {deleting ? 'Deleting…' : 'Delete category'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CategoriesAdmin;
